// View + edit a company location's shipping address. Saving writes back to Shopify.
// scope: undefined/{} for a client (their own location) or { store, companyLocationId } for
// a rep/staff acting on a specific client location.
import { useEffect, useState } from 'react';
import { fetchShippingAddress, updateShippingAddress } from '../api/me.js';

const FIELDS = [
  { key: 'recipient', label: 'Recipient / attention', full: true },
  { key: 'address1', label: 'Address line 1', full: true },
  { key: 'address2', label: 'Address line 2', full: true },
  { key: 'city', label: 'City' },
  { key: 'zoneCode', label: 'State / province code', placeholder: 'e.g. NSW, CA' },
  { key: 'zip', label: 'Postcode / ZIP' },
  { key: 'countryCode', label: 'Country code', placeholder: 'e.g. AU, US' },
  { key: 'phone', label: 'Phone', full: true },
];
const BLANK = Object.fromEntries(FIELDS.map((f) => [f.key, '']));
const errMsg = (e) => e.response?.data?.error?.message || 'Something went wrong.';

export default function ShippingAddressCard({ scope, title = 'Shipping address', readOnly = false }) {
  const [form, setForm] = useState(BLANK);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [locationName, setLocationName] = useState('');

  const scopeKey = scope ? `${scope.store}:${scope.companyLocationId}` : 'self';

  const load = () => {
    setLoading(true);
    setError('');
    fetchShippingAddress(scope)
      .then((d) => {
        setForm({ ...BLANK, ...(d.address || {}) });
        setLocationName(d.locationName || '');
      })
      .catch((e) => setError(errMsg(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, [scopeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const d = await updateShippingAddress(scope, form);
      setForm({ ...BLANK, ...(d.address || {}) });
      setNotice(d.simulated ? 'Saved (test environment — not sent to Shopify).' : 'Saved.');
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-xl rounded-lg border border-line bg-surface p-5 shadow-sm">
      <h2 className="text-sm font-semibold text-text-primary">{title}</h2>
      <p className="mt-0.5 text-xs text-text-muted">{readOnly ? 'Where this order ships.' : 'Where orders ship.'}</p>
      {locationName && (
        <p className="mt-0.5 text-xs text-text-muted">
          Store: <span className="font-medium text-text-secondary">{locationName}</span>
        </p>
      )}

      {loading ? (
        <p className="mt-4 text-sm text-text-muted">Loading…</p>
      ) : readOnly ? (
        error ? (
          <p className="mt-4 text-sm text-error">{error}</p>
        ) : FIELDS.every((f) => !form[f.key]) ? (
          <p className="mt-4 text-sm text-text-muted">No shipping address on file for this location.</p>
        ) : (
          <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            {FIELDS.filter((f) => form[f.key]).map((f) => (
              <div key={f.key} className={f.full ? 'col-span-2' : ''}>
                <dt className="text-xs text-text-muted">{f.label}</dt>
                <dd className="text-text-primary">{form[f.key]}</dd>
              </div>
            ))}
          </dl>
        )
      ) : (
        <form onSubmit={save} className="mt-4 grid grid-cols-2 gap-3">
          {FIELDS.map((f) => (
            <div key={f.key} className={f.full ? 'col-span-2' : ''}>
              <label className="block text-xs font-medium text-text-secondary">{f.label}</label>
              <input
                value={form[f.key] ?? ''}
                onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                placeholder={f.placeholder || ''}
                className="mt-1 w-full rounded-md border border-line bg-surface px-3 py-2 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
              />
            </div>
          ))}
          {error && <p className="col-span-2 text-sm text-error">{error}</p>}
          {notice && <p className="col-span-2 text-sm text-success">{notice}</p>}
          <div className="col-span-2 flex items-center gap-2">
            <button
              type="submit"
              disabled={saving}
              className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save address'}
            </button>
            <button
              type="button"
              onClick={load}
              disabled={saving}
              className="rounded-md border border-line px-3 py-2 text-sm font-medium text-text-secondary hover:bg-surface-raised"
            >
              Reset
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
